import { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { formatFileSize } from '../utils/helpers';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../utils/constants';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  uploading: boolean;
  selectedFile: File | null;
}

const FileUpload = forwardRef<{ triggerFileSelect: () => void }, FileUploadProps>(({ onFileSelect, uploading, selectedFile }, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    triggerFileSelect: () => {
      fileInputRef.current?.click();
    }
  }));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const getAcceptedFormats = () => {
    return '.docx,.txt,.md,.markdown';
  };

  return (
    <div className="space-y-6">
      <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
            dragActive 
              ? 'border-blue-400 bg-blue-50' 
              : uploading 
                ? 'border-gray-300 bg-gray-50' 
                : 'border-blue-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          className="hidden"
          accept={getAcceptedFormats()}
          disabled={uploading}
        />

        <div className="space-y-4">
          {selectedFile ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center text-green-600">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-1">
                <div className="text-lg font-medium text-gray-700">File Selected</div>
                <div className="text-sm text-gray-600">{selectedFile.name}</div>
                <div className="text-xs text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </div>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`px-4 py-2 rounded-md text-sm transition-colors ${
                  uploading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Choose Different File
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center text-gray-400">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div className="space-y-2">
                <div className="text-gray-600">
                  Drag and drop your file here
                </div>
                <div className="text-sm text-gray-500">
                  or
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className={`px-6 py-3 rounded-md transition-colors font-medium ${
                    uploading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {uploading ? 'Processing...' : 'Choose File to Upload'}
                </button>
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <div>Supported formats: DOCX, TXT, Markdown</div>
                <div>Maximum file size: {MAX_FILE_SIZE / 1024 / 1024}MB</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
